import { generateBetItemId } from '../utils/ticketHelpers';
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BetItem } from './BettingSlipDrawer';
import { getPreEvents, getTournaments } from '../api/api';
import { EventSnapshot, Market, Outcome, TournamentItem } from '../api/types';
import { OddsButton } from './OddsButton';
import MatchDetailPage from './MatchDetailPage';
import { 
  find1x2Market, 
  findRestzeitGeneric, 
  findOUGeneric, 
  findNextGoal, 
  findHandicap, 
  findBTTSGeneric, 
  findDCGeneric, 
  findH2H,
  find1stHalfMarket,
  getMarketAttr,
  isFilterAvailable,
  getMarketOptionsForSport,
  getLayoutGroupForMarketId,
  formatTippLabelWithAttr
} from '../utils/marketParsers';
import { useTranslation } from '../i18n';
import { translateMarketFilterLabel, formatOddsButtonLabel } from '../utils/marketTranslations';
import { mergeSnapshotWithPatch, applyStreamUpdates } from '../utils/streamMerge';
import { preStreamManager } from '../services/CatalogStreamManager';
import { COUNTRY_FLAGS, EN_TO_DE_COUNTRIES, COUNTRY_PRIORITY } from '../constants/countries';
import { SPORT_PRIORITIES } from '../constants/tipico_menu';
import { CountryFlag } from './CountryFlag';
import { getFlagCodeFromAlpha3 } from '../utils/flagUtils';

interface SportMatchesPageProps {
  sportLabel: string;
  sportEmoji: string;
  sportId: string;
  timeFilter: string;
  onBack: () => void;
  isLogged: boolean;
  onLoginClick: () => void;
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onUpdateBet?: (id: string, patch: Partial<BetItem>) => void;
  initialTournamentName?: string | null;
  onClearInitialCompetition?: () => void;
  initialCategory?: string;
  initialTournament?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function countryCodeToFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  return String.fromCodePoint(upper.charCodeAt(0) + 127397, upper.charCodeAt(1) + 127397);
}

const KNOWN_FLAGS: Record<string, string> = {
  International: '🌍', 'Champions League': '🏆', 'Europa League': '🏆',
  'UEFA Champions League': '🏆', 'UEFA Europa League': '🏆',
  UEFA: '🏆', 'World Cup': '🌍', 'Copa America': '🌎',
  'Africa Cup': '🌍', 'Club World Cup': '🏆',
};

function categoryFlag(name: string, cc?: string): string {
  if (KNOWN_FLAGS[name]) return KNOWN_FLAGS[name];
  const deName = EN_TO_DE_COUNTRIES[name] || name;
  if (COUNTRY_FLAGS[deName]) return COUNTRY_FLAGS[deName];
  // The API sends alpha-3 ('DEU'); the emoji trick needs alpha-2.
  const alpha2 = getFlagCodeFromAlpha3(cc);
  if (alpha2 && alpha2.length === 2) return countryCodeToFlag(alpha2);
  // No country code at all means a non-national competition (International /
  // Uluslararası / …), which reads better as a globe than a blank flag.
  return cc ? '🏳️' : '🌍';
}


const LockIcon = () => (<svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>);

function formatOdds(val: string | number | undefined): string {
  if (!val) return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

function find1x2(markets: Market[]): Market | undefined {
  return markets.find((m) => m.market_id === '1' && m.status !== -1);
}

function getOutcome(market: Market, id: string): Outcome | undefined {
  return market.outcomes.find((o) => o.outcome_id === id && o.active);
}

function formatEventTime(ev: EventSnapshot): string {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return '';
  return new Date(starts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function isWithinHours(ev: EventSnapshot, hours: number): boolean {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return false;
  const d = new Date(starts).getTime();
  const now = Date.now();
  return d >= now && d <= now + hours * 3600_000;
}

// ── Data models ────────────────────────────────────────────────────────────

interface CategoryEntry { name: string; flag: string; count: number; countryCode?: string; }
interface TournamentEntry { name: string; flag: string; count: number; }
interface MatchEntry {
  id: string; time: string; team1: string; team2: string;
  odds: { home: string; draw: string; away: string };
  marketId: string; moreCount: number;
}
interface LeagueGroup { name: string; matches: MatchEntry[]; }

function buildCategories(tournaments: TournamentItem[]): CategoryEntry[] {
  const map = new Map<string, number>();
  // Category names arrive localized, so keep the API's country code alongside
  // them — it is the only language-independent way to pick the right flag.
  const codes = new Map<string, string>();
  for (const t of tournaments) {
    const cat = t.category_name ?? 'Other';
    map.set(cat, (map.get(cat) ?? 0) + t.event_count);
    if (t.country_code && !codes.has(cat)) codes.set(cat, t.country_code);
  }
  return Array.from(map.entries()).map(([name, count]) => ({
    name, flag: categoryFlag(name, codes.get(name)), count, countryCode: codes.get(name),
  }));
}

function buildTournaments(tournaments: TournamentItem[], categoryName: string, catFlag: string): TournamentEntry[] {
  return tournaments
    .filter(t => t.category_name === categoryName)
    .map(t => ({
      name: t.tournament_name || 'Other',
      flag: catFlag,
      count: t.event_count
    }));
}

function buildLeagueGroups(events: EventSnapshot[]): LeagueGroup[] {
  const map = new Map<string, MatchEntry[]>();
  const earliest = new Map<string, number>();
  for (const ev of events) {
    const market = find1x2(ev.markets ?? []);
    const homeO = market ? getOutcome(market, '1') : undefined;
    const drawO = market ? getOutcome(market, '2') : undefined;
    const awayO = market ? getOutcome(market, '3') : undefined;
    const entry: MatchEntry = {
      id: ev.event_id,
      time: formatEventTime(ev),
      team1: ev.event?.home?.name ?? 'Home',
      team2: ev.event?.away?.name ?? 'Away',
      odds: {
        home: formatOdds(homeO?.odds),
        draw: formatOdds(drawO?.odds),
        away: formatOdds(awayO?.odds),
      },
      marketId: market?.market_id ?? '1',
      moreCount: (ev.markets ?? []).length,
    };
    const tourney = ev.event?.tournament_name ?? 'Other';
    if (!map.has(tourney)) map.set(tourney, []);
    map.get(tourney)!.push(entry);
    const ms = eventStartMs(ev);
    if (!earliest.has(tourney) || ms < earliest.get(tourney)!) earliest.set(tourney, ms);
  }
  // Order leagues by their earliest match so the "Alle Spiele" list reads
  // chronologically instead of in per-tournament fetch-completion order.
  return Array.from(map.entries())
    .map(([name, matches]) => ({ name, matches }))
    .sort((a, b) => (earliest.get(a.name) ?? Infinity) - (earliest.get(b.name) ?? Infinity));
}

const JerseyIcon = ({ color, stripeColor }: { color: string; stripeColor?: string }) => (
  <svg className="w-5 h-4 flex-shrink-0" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 3 L8 1 L16 1 L20 3 L23 6 L20 9 L18 8 L18 19 A1 1 0 0 1 17 20 L7 20 A1 1 0 0 1 6 19 L6 8 L4 9 L1 6 Z" fill={color} stroke="#cbd5e1" strokeWidth="1" />
    <path d="M10 1 L12 4 L14 1" stroke="#cbd5e1" strokeWidth="1" />
    {stripeColor && (
      <>
        <line x1="10" y1="5" x2="10" y2="18" stroke={stripeColor} strokeWidth="1.5" />
        <line x1="12" y1="5" x2="12" y2="18" stroke={stripeColor} strokeWidth="1.5" />
        <line x1="14" y1="5" x2="14" y2="18" stroke={stripeColor} strokeWidth="1.5" />
      </>
    )}
  </svg>
);

function getJerseyColors(teamName: string, isHome: boolean): { color: string; stripeColor?: string } {
  const name = teamName.toLowerCase();
  
  if (name.includes('essen') || name.includes('bayern') || name.includes('köln') || name.includes('arsenal') || name.includes('liverpool') || name.includes('manchester utd')) {
    return { color: '#e11d48' }; // red
  }
  if (name.includes('greuther') || name.includes('fürth') || name.includes('real madrid') || name.includes('chelsea') || name.includes('leipzig')) {
    if (isHome) return { color: '#f8fafc', stripeColor: '#64748b' }; // white with gray stripes
  }
  
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = [
    { color: '#e11d48' }, // Red
    { color: '#2563eb' }, // Blue
    { color: '#16a34a' }, // Green
    { color: '#ca8a04' }, // Yellow
    { color: '#f8fafc', stripeColor: '#64748b' }, // White with gray stripes
    { color: '#1e293b' }, // Dark slate
    { color: '#ea580c' }, // Orange
    { color: '#9333ea' }, // Purple
  ];
  
  const index = Math.abs(hash) % colors.length;
  if (!isHome) {
    const awayIndex = (index + 3) % colors.length;
    return colors[awayIndex];
  }
  return colors[index];
}

const StatsIcon = () => (
  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 transition cursor-pointer" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 11h2v6H2v-6zm4-4h2v10H6V7zm4-5h2v15h-2V2zm4 8h2v7h-2v-7z" />
  </svg>
);


function formatMatchDateHeader(scheduledAt?: string): string {
  if (!scheduledAt) return 'Spieltag';
  const d = new Date(scheduledAt);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const dateStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

  if (d.toDateString() === today.toDateString()) {
    return `Heute, ${dateStr}`;
  } else if (d.toDateString() === tomorrow.toDateString()) {
    return `Morgen, ${dateStr}`;
  } else {
    const weekday = d.toLocaleDateString('de-DE', { weekday: 'long' });
    return `${weekday}, ${dateStr}`;
  }
}

function eventStartMs(ev: EventSnapshot): number {
  const t = ev.event?.scheduled_at ?? ev.event?.start_time;
  const n = t ? new Date(t).getTime() : NaN;
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

function groupEventsByDate(events: EventSnapshot[]): { dateStr: string; events: EventSnapshot[] }[] {
  // Events are merged from several per-tournament fetches, so their array order
  // is not chronological. Sort by start time first, then group by day — this
  // keeps both the date headers and the matches within each day in order.
  const sorted = [...events].sort((a, b) => eventStartMs(a) - eventStartMs(b));
  const groups: Record<string, EventSnapshot[]> = {};
  const order: string[] = [];
  for (const ev of sorted) {
    const dateStr = formatMatchDateHeader(ev.event?.scheduled_at ?? ev.event?.start_time);
    if (!groups[dateStr]) {
      groups[dateStr] = [];
      order.push(dateStr);
    }
    groups[dateStr].push(ev);
  }
  return order.map((dateStr) => ({ dateStr, events: groups[dateStr] }));
}


const TIME_FILTERS = [
  { id: 'alle', label: 'Alle', hours: 99999, image: '/images/48-hours.png' },
  { id: 'heute', label: 'Heute', hours: 24, image: '/images/haute.png' },
  { id: '3std', label: '3 Std.', hours: 3, image: '/images/3-hours.png' },
  { id: '48std', label: '48 Std.', hours: 48, image: '/images/48-hours.png' },
];

const SPORT_ID_TO_NAME: Record<string, string> = {
  'sr:sport:1': 'Soccer',
  'sr:sport:2': 'Basketball',
  'sr:sport:5': 'Tennis',
  'sr:sport:4': 'Ice Hockey',
  'sr:sport:6': 'Handball',
};

// ── Component ──────────────────────────────────────────────────────────────

const SportMatchesPage: React.FC<SportMatchesPageProps> = ({
  sportLabel, sportEmoji, sportId, timeFilter, onBack, isLogged, onLoginClick, bets, onAddBet, onRemoveBet, onUpdateBet,
  initialTournamentName, onClearInitialCompetition,
  initialCategory, initialTournament,
}) => {
  const { locale } = useTranslation();
  const [activeFilter, setActiveFilter] = useState(timeFilter || 'alle');
  const [allPreEvents, setAllPreEvents] = useState<EventSnapshot[]>([]);
  const [fetchedTournaments, setFetchedTournaments] = useState<TournamentItem[]>([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Read category/tournament from path params (passed as props from SportPage)
  const categoryParam = initialCategory ? decodeURIComponent(initialCategory) : null;
  const tournamentParam = initialTournament ? decodeURIComponent(initialTournament) : null;
  const showMatches = !!categoryParam && !!tournamentParam;
  const selectedTournament = tournamentParam;

  const setSelectedCategory = (cat: CategoryEntry | null) => {
    if (cat) {
      navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(cat.name)}`);
    } else {
      navigate(`/sport/${encodeURIComponent(sportLabel)}`);
    }
  };

  const setSelectedTournament = (tourney: string | null) => {
    if (tourney && categoryParam) {
      navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(categoryParam)}/${encodeURIComponent(tourney)}`);
    } else if (categoryParam) {
      navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(categoryParam)}`);
    } else {
      navigate(`/sport/${encodeURIComponent(sportLabel)}`);
    }
  };

  const setShowMatches = (show: boolean) => {
    if (!show && categoryParam) {
      navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(categoryParam)}`);
    }
  };
  const [selectedEvent, setSelectedEvent] = useState<EventSnapshot | null>(null);



  // Opening a match swaps this whole view for MatchDetailPage, which scrolls to
  // the top on mount. Remember where the list was so closing the detail returns
  // to the same spot instead of the top. The list data stays in state, so the
  // page is already full height when we restore.
  const scrollBeforeDetailRef = useRef(0);

  const handleEventClick = (ev: EventSnapshot) => {
    scrollBeforeDetailRef.current = window.scrollY;
    setSelectedEvent(ev);
  };

  useLayoutEffect(() => {
    if (selectedEvent) return;
    const targetY = scrollBeforeDetailRef.current;
    if (targetY <= 0) return;
    scrollBeforeDetailRef.current = 0;

    // Applied before paint; retry briefly in case rows are still settling.
    let tries = 0;
    const apply = () => {
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo(0, Math.min(targetY, maxScroll));
      if (maxScroll < targetY && ++tries < 20) timer = setTimeout(apply, 50);
    };
    let timer: ReturnType<typeof setTimeout>;
    apply();
    return () => clearTimeout(timer);
  }, [selectedEvent]);
  const sportName = SPORT_ID_TO_NAME[sportId] || 'Soccer';
  const sportFilters = useMemo(
    () => getMarketOptionsForSport(sportName, activeFilter === 'live'),
    [sportName, activeFilter]
  );
  const [activeBetFilter, setActiveBetFilter] = useState<string | number>(() => sportFilters[0] || 'Tipp');

  useEffect(() => {
    if (showMatches) return;
    setLoading(true);
    let within_hours = 2160;
    if (activeFilter === 'heute') within_hours = 24;
    else if (activeFilter === '3std') within_hours = 3;
    else if (activeFilter === '48std') within_hours = 48;

    getTournaments({ sport_id: sportId, within_hours })
      .then(res => setFetchedTournaments(res.items || []))
      .catch(() => setFetchedTournaments([]))
      .finally(() => setLoading(false));
  }, [sportId, activeFilter, showMatches]);

  useEffect(() => {
    if (!showMatches) return;
    setLoading(false);
    const abortController = new AbortController();

    // The match list is populated by the country/league backfill effect above and
    // kept current here via pre-stream patches only. We deliberately do NOT
    // subscribe to the live stream (or fetch live events): live matches aren't
    // shown on this page, and an idle live subscription would keep the live SSE
    // open on a non-live page, triggering a per-event resync storm
    // (GET /catalog/events/{id}?scope=live) for every live match.
    const unsubPre = preStreamManager.subscribe((upserts, deletes) => {
      if (abortController.signal.aborted) return;
      if (upserts.length > 0 || deletes.length > 0) {
        const applyUpdates = applyStreamUpdates(upserts, deletes, false);
        setAllPreEvents(prev => applyUpdates(prev) || []);
      }
    });

    return () => {
      abortController.abort();
      unsubPre();
    };
    // activeFilter is intentionally excluded: the time window is applied
    // client-side, so changing it must not tear down and re-open the SSE stream.
  }, [sportId, showMatches]);

  // Active events for current filter (ONLY USED WHEN SHOWMATCHES IS TRUE)
  const activeEvents: EventSnapshot[] = useMemo(() => {
    if (!showMatches) return [];
    if (activeFilter === 'alle') return allPreEvents;
    const hours = TIME_FILTERS.find((f) => f.id === activeFilter)?.hours ?? 24;
    return allPreEvents.filter((e) => isWithinHours(e, hours));
  }, [showMatches, activeFilter, allPreEvents]);

  // Category list
  const categories = useMemo(() => buildCategories(fetchedTournaments), [fetchedTournaments]);
  // Deep-linked category that isn't in the built list yet still has its code here.
  const fallbackCategoryCode = useMemo(
    () => fetchedTournaments.find((t) => t.category_name === categoryParam)?.country_code,
    [fetchedTournaments, categoryParam]
  );
  const selectedCategory = useMemo(() => (
    categoryParam
      ? (categories.find((c) => c.name === categoryParam) || {
          name: categoryParam,
          flag: categoryFlag(categoryParam, fallbackCategoryCode),
          count: 0,
          countryCode: fallbackCategoryCode,
        })
      : null
  ), [categoryParam, categories, fallbackCategoryCode]);

  useEffect(() => {
    if (initialTournamentName && fetchedTournaments.length > 0 && !selectedCategory) {
      const matchingTourney = fetchedTournaments.find(t => t.tournament_name === initialTournamentName);
      if (matchingTourney) {
        const catName = matchingTourney.category_name ?? 'Other';
        setSelectedCategory({
          name: catName,
          flag: categoryFlag(catName, matchingTourney.country_code),
          count: fetchedTournaments.filter(t => t.category_name === catName).reduce((sum, t) => sum + t.event_count, 0),
          countryCode: matchingTourney.country_code,
        });
        setSelectedTournament(initialTournamentName);
        setShowMatches(true);
        if (onClearInitialCompetition) {
          onClearInitialCompetition();
        }
      }
    }
  }, [initialTournamentName, fetchedTournaments, selectedCategory, onClearInitialCompetition]);

  // Backfill events for the selected country/league via per-tournament queries
  // (pre/events?tournament_id=...): "Alle Spiele" fetches every league of the
  // country, a single-league selection fetches just that one. A ref-based key
  // guard runs each selection only once, so it no longer loops or flickers the
  // list the way it did when selectedCategory was an unstable reference.
  const fetchedCategoryKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!showMatches || !selectedCategory || fetchedTournaments.length === 0) return;

    // All tournaments (leagues) that belong to the selected country.
    const categoryTournaments = fetchedTournaments.filter(
      t => t.category_name === selectedCategory.name && t.tournament_id
    );
    if (categoryTournaments.length === 0) return;

    // "Alle Spiele" → fetch every league of the country; single league → just that one.
    const isSingleLeague = !!selectedTournament && selectedTournament !== 'alle';
    const targetTournaments = isSingleLeague
      ? categoryTournaments.filter(t => t.tournament_name === selectedTournament)
      : categoryTournaments;
    if (targetTournaments.length === 0) return;

    // Fetch each league only once per selection (prevents re-fetch loops / flicker).
    // sportId is part of the key so the same category/league name in different
    // sports does not collide. The time filter is applied client-side, so it is
    // not part of the key — backfill fetches every match regardless of filter.
    const key = `${sportId}|${selectedCategory.name}|${isSingleLeague ? selectedTournament : 'alle'}`;
    if (fetchedCategoryKeys.current.has(key)) return;
    fetchedCategoryKeys.current.add(key);

    let active = true;
    Promise.all(
      targetTournaments.map(t =>
        getPreEvents({ tournament_id: t.tournament_id }).catch(() => ({ items: [] as EventSnapshot[] }))
      )
    )
      .then(results => {
        if (!active) return;
        const newEvents = results.flatMap(r => r.items || []);
        if (newEvents.length === 0) return;
        setAllPreEvents(prev => {
          const map = new Map(prev.map(e => [e.event_id, e]));
          newEvents.forEach(e => map.set(e.event_id, e));
          return Array.from(map.values());
        });
      })
      .catch(() => { fetchedCategoryKeys.current.delete(key); });

    return () => { active = false; };
  }, [showMatches, selectedCategory, selectedTournament, sportId, fetchedTournaments]);

  const deSportName = EN_TO_DE_COUNTRIES[sportLabel] || sportLabel;
  const basePriorities = SPORT_PRIORITIES[deSportName] || COUNTRY_PRIORITY;
  const mobilePriorities = [
    'Champions League', 'UEFA Champions League', 'Europa League', 'UEFA Europa League', 'International',
    ...basePriorities
  ];

  const topCategories = categories
    .filter((c) => {
      const deName = EN_TO_DE_COUNTRIES[c.name] || c.name;
      return mobilePriorities.includes(c.name) || mobilePriorities.includes(deName);
    })
    .sort((a, b) => {
      const deNameA = EN_TO_DE_COUNTRIES[a.name] || a.name;
      const deNameB = EN_TO_DE_COUNTRIES[b.name] || b.name;
      const idxA = mobilePriorities.indexOf(a.name) !== -1 ? mobilePriorities.indexOf(a.name) : mobilePriorities.indexOf(deNameA);
      const idxB = mobilePriorities.indexOf(b.name) !== -1 ? mobilePriorities.indexOf(b.name) : mobilePriorities.indexOf(deNameB);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
    
  const azCategories = categories
    .filter((c) => {
      const deName = EN_TO_DE_COUNTRIES[c.name] || c.name;
      return !mobilePriorities.includes(c.name) && !mobilePriorities.includes(deName);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Tournament list for selected category
  const tournaments = selectedCategory
    ? buildTournaments(fetchedTournaments, selectedCategory.name, selectedCategory.flag)
    : [];

  const categoryEvents: EventSnapshot[] = useMemo(() => (
    selectedCategory
      ? activeEvents.filter((e: EventSnapshot) =>
          e.event?.category_name === selectedCategory.name && e.event?.sport_id === sportId)
      : []
  ), [selectedCategory, activeEvents, sportId]);

  // Match list events (filtered by tournament if one is selected)
  const matchEvents: EventSnapshot[] = useMemo(() => (
    showMatches
      ? (selectedTournament && selectedTournament !== 'alle'
        ? categoryEvents.filter((e: EventSnapshot) => e.event?.tournament_name === selectedTournament)
        : categoryEvents)
      : []
  ), [showMatches, selectedTournament, categoryEvents]);

  const availableFilters = sportFilters.filter((f) => matchEvents.some((ev: EventSnapshot) => isFilterAvailable(f, ev.markets)));

  useEffect(() => {
    const available = sportFilters.filter((f) => matchEvents.some((ev: EventSnapshot) => isFilterAvailable(f, ev.markets)));
    if (available.length > 0) {
      if (!available.includes(activeBetFilter)) {
        setActiveBetFilter(available[0]);
      }
    } else if (sportFilters.length > 0 && !sportFilters.includes(activeBetFilter)) {
      setActiveBetFilter(sportFilters[0]);
    }
  }, [matchEvents, activeBetFilter, sportFilters]);

  const displayedMatchEvents = matchEvents.filter((ev: EventSnapshot) => isFilterAvailable(activeBetFilter, ev.markets));
  const leagues = buildLeagueGroups(displayedMatchEvents);

  // ── Odds handlers ────────────────────────────────────────────────────────

  const isOddsSelected = (eventId: string, type: 'home' | 'draw' | 'away') =>
    bets.some((b) => b.id === `pre-${eventId}-${type}`);

  const handleOddsClick = (match: MatchEntry, type: 'home' | 'draw' | 'away') => {
    const id = `pre-${match.id}-${type}`;
    if (isOddsSelected(match.id, type)) {
      onRemoveBet(id);
    } else {
      const tippLabels = { home: 'Heimsieg', draw: 'Unentschieden', away: 'Auswärtssieg' };
      onAddBet({
        id,
        matchId: match.id,
        matchName: `${match.team1} - ${match.team2}`,
        betType: type,
        tippLabel: tippLabels[type],
        odds: match.odds[type],
        eventId: match.id,
        marketId: match.marketId,
        outcomeId: type === 'home' ? '1' : type === 'draw' ? '2' : '3',
      });
    }
  };

  // Flexible odds handler for all markets
  const isEvSelected = (eventId: string, outcomeId: string, specifiers?: Record<string, string>) =>
    bets.some((b) => b.id === generateBetItemId(eventId, '1', outcomeId, specifiers));

  const handleEvOddsClick = (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => {
    const id = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
    if (isEvSelected(ev.event_id, outcome.outcome_id, market.specifiers)) {
      onRemoveBet(id);
    } else {
      const formattedTipp = formatTippLabelWithAttr(tippLabel, market);
      onAddBet({
        id,
        matchId: 0,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: 'home',
        tippLabel: formattedTipp,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        outcomeName: (outcome as any).player_name || (outcome as any).playerName || outcome.name,
        specifiers: market.specifiers
          ? typeof market.specifiers === 'string'
            ? market.specifiers
            : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  // ── Sub-renders ──────────────────────────────────────────────────────────

  const renderHeader = (title: string, backFn: () => void) => (
    <div className="bg-[#f5f6f6] dark:bg-[#1a1a1a] px-4 py-3 flex items-center gap-2 border-b border-gray-100 dark:border-[#333] sticky top-0 z-30">
      <button onClick={backFn} className="text-gray-800 dark:text-gray-200 p-1 flex-shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <span className="text-base font-bold text-gray-900 dark:text-white truncate px-2">{title}</span>
      {!isLogged && (
        <button onClick={onLoginClick} className="ml-auto text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-500 transition flex-shrink-0">
          Anmelden
        </button>
      )}
    </div>
  );

  const renderTimeTabs = (onFilterChange?: () => void) => (
    <div className="bg-[#f5f6f6] dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-[#333] px-4 py-3 flex gap-4 w-full">
      {TIME_FILTERS.map((f) => {
        const active = activeFilter === f.id;
        return (
          <button
            key={f.id}
            onClick={() => {
              setActiveFilter(f.id);
              setSelectedTournament(null);
              setShowMatches(false);
              if (onFilterChange) onFilterChange();
            }}
            className={`flex flex-col items-center w-1/4 pb-2 border-b-2 transition ${active ? 'border-red-600' : 'border-transparent'
              }`}
          >
            <div className="relative mb-1">
              <img src={f.image} alt={f.label} className="w-10 h-10 object-contain rounded-[4px]" />
            </div>
            <span className={`text-xs font-semibold ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}>{f.label}</span>
          </button>
        );
      })}
    </div>
  );

  const renderCategoryRow = (cat: CategoryEntry, idx: number, arr: CategoryEntry[]) => (
    <button
      key={cat.name}
      onClick={() => setSelectedCategory(cat)}
      className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition ${idx < arr.length - 1 ? 'border-b border-gray-100' : ''
        }`}
    >
      <CountryFlag countryName={cat.name} countryCode={cat.countryCode} fallbackEmoji={cat.flag} className="text-2xl text-center flex-shrink-0 leading-none" />
      <span className="flex-1 text-[15px] font-medium text-gray-900">{cat.name}</span>
      <span className="text-[15px] font-medium text-gray-600">{cat.count}</span>
    </button>
  );

  // ── VIEW 4: Match detail ─────────────────────────────────────────────────

  if (selectedEvent) {
    return (
      <MatchDetailPage
        event={selectedEvent}
        onBack={() => setSelectedEvent(null)}
        bets={bets}
        onAddBet={onAddBet}
        onRemoveBet={onRemoveBet}
        onUpdateBet={onUpdateBet}
        onLoginClick={onLoginClick}
      />
    );
  }

  // ── VIEW 3: Match list ───────────────────────────────────────────────────

  if (showMatches && selectedCategory) {
    const title = selectedTournament
      ? `${sportLabel} ${selectedTournament}`
      : `${sportLabel} ${selectedCategory.name}`;
    return (
      <div className="min-h-screen bg-gray-100 pb-20">
        {renderHeader(title, () => {
          if (selectedTournament && categoryParam) {
            navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(categoryParam)}`);
          } else if (categoryParam) {
            navigate(`/sport/${encodeURIComponent(sportLabel)}`);
          }
        })}
        {selectedTournament ? (
          sportId === 'sr:sport:1' ? (
            <div id="tournament-tabs" data-testid="tournament-tabs" role="tablist" className="tab-menu default tab-xs py-[5px] border-b border-solid border-standard">
              {/* Spiele - Active tab with Bundesliga-style shield icon */}
              <button id="tournament-matches" data-testid="tournament-matches" role="tab" aria-selected="true" className="tab tab-selected flex-grow min-w-[60px] bg-surface">
                <svg className="tab-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="2" width="22" height="24" rx="3" fill="#ffffff" stroke="#c8102e" strokeWidth="1.5"/>
                  <rect x="3" y="2" width="22" height="12" rx="3" fill="#c8102e"/>
                  <text x="14" y="12" textAnchor="middle" fill="#ffffff" fontSize="7" fontWeight="bold">BL</text>
                  <text x="14" y="22" textAnchor="middle" fill="#c8102e" fontSize="6" fontWeight="bold">2</text>
                </svg>
                <div className="tab-container"><span className="tab-content"><span>Spiele</span></span></div>
              </button>
              {/* MatchKombi+ - Red cross/plus combination icon */}
              <button id="match-kombi" data-testid="match-kombi" role="tab" aria-selected="false" className="tab flex-grow min-w-[60px] bg-surface">
                <svg className="tab-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="4" width="11" height="11" rx="2" fill="#2563eb"/>
                  <rect x="15" y="13" width="11" height="11" rx="2" fill="#1e293b"/>
                  <circle cx="19" cy="9" r="6" fill="#c8102e"/>
                  <path d="M19 6v6M16 9h6" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <div className="tab-container"><span className="tab-content"><span>MatchKom...</span></span></div>
              </button>
              {/* Statistiken - Red bar chart icon */}
              <button id="statistics-matches" data-testid="statistics-matches" role="tab" aria-selected="false" className="tab flex-grow min-w-[60px] bg-surface">
                <svg className="tab-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="16" width="4" height="9" rx="1" fill="#c8102e" opacity="0.5"/>
                  <rect x="9" y="10" width="4" height="15" rx="1" fill="#c8102e" opacity="0.7"/>
                  <rect x="15" y="4" width="4" height="21" rx="1" fill="#c8102e" opacity="0.85"/>
                  <rect x="21" y="12" width="4" height="13" rx="1" fill="#c8102e"/>
                </svg>
                <div className="tab-container"><span className="tab-content"><span>Statistiken</span></span></div>
              </button>
              {/* Siegwetten - Golden trophy/coins icon */}
              <button id="outrights-matches" data-testid="outrights-matches" role="tab" aria-selected="false" className="tab flex-grow min-w-[60px] bg-surface">
                <svg className="tab-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6h12v3c0 3.3-2.7 6-6 6s-6-2.7-6-6V6z" fill="#eab308" stroke="#ca8a04" strokeWidth="1"/>
                  <path d="M6 6h2v3c-1.1 0-2-.9-2-2V6zm14 0h2v1c0 1.1-.9 2-2 2V6z" fill="#eab308" opacity="0.7"/>
                  <rect x="12" y="15" width="4" height="4" fill="#ca8a04"/>
                  <rect x="9" y="19" width="10" height="3" rx="1" fill="#eab308"/>
                  <circle cx="11" cy="24" r="1.5" fill="#eab308"/>
                  <circle cx="14" cy="24" r="1.5" fill="#ca8a04"/>
                  <circle cx="17" cy="24" r="1.5" fill="#eab308"/>
                </svg>
                <div className="tab-container"><span className="tab-content"><span>Siegwetten</span></span></div>
              </button>
              {/* Wett-Guide - Red book with question mark */}
              <button id="bet-guide" data-testid="bet-guide" role="tab" aria-selected="false" className="tab flex-grow min-w-[60px] bg-surface">
                <svg className="tab-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="3" width="20" height="22" rx="2" fill="#c8102e" opacity="0.15"/>
                  <rect x="6" y="3" width="18" height="22" rx="2" fill="#ffffff" stroke="#c8102e" strokeWidth="1.2"/>
                  <rect x="6" y="3" width="4" height="22" fill="#c8102e" opacity="0.8"/>
                  <text x="18" y="18" textAnchor="middle" fill="#c8102e" fontSize="12" fontWeight="bold">?</text>
                </svg>
                <div className="tab-container"><span className="tab-content"><span>Wett-Guide</span></span></div>
              </button>
            </div>
          ) : null
        ) : (
          renderTimeTabs(() => setSelectedCategory(null))
        )}
        <div className="px-3 pt-3">
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
            {availableFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveBetFilter(filter)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${activeBetFilter === filter
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300'
                  }`}
              >
                {translateMarketFilterLabel(filter, locale)}
              </button>
            ))}
          </div>
          
          {displayedMatchEvents.length === 0 && (
            <p className="text-center text-gray-500 text-sm mt-10">Keine Ereignisse verfügbar.</p>
          )}

          <div className="flex flex-col gap-3">
            {selectedTournament ? (
              // Single Tournament view: Group directly by Date (each Date is a card)
              groupEventsByDate(displayedMatchEvents).map((dateGroup) => (
                <div key={dateGroup.dateStr} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="px-4 py-3 border-b border-gray-100 bg-white">
                    <span className="text-sm font-bold text-gray-900">{dateGroup.dateStr}</span>
                  </div>
                  {dateGroup.events.map((ev, idx) => {
                    const matchTime = formatEventTime(ev);
                    const team1 = ev.event?.home?.name ?? 'Home';
                    const team2 = ev.event?.away?.name ?? 'Away';
                    const moreCount = ev.markets?.length ?? 0;
                    const homeJersey = getJerseyColors(team1, true);
                    const awayJersey = getJerseyColors(team2, false);
                    const homeScore = ev.status?.home_score;
                    const awayScore = ev.status?.away_score;
                    const hasScores = typeof homeScore === 'number' && typeof awayScore === 'number';

                    let attrMarket: Market | undefined;
                    const layoutGrp1 = getLayoutGroupForMarketId(activeBetFilter);
                    if (layoutGrp1 === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Handicap' || layoutGrp1 === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === 'Head-To-Head' || layoutGrp1 === 'Sieger' || layoutGrp1 === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                    else if (layoutGrp1 === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                    const attr = attrMarket ? getMarketAttr(attrMarket) : null;

                    const renderOddsButtons = (items: { label: string; outcome: Outcome; market: Market; tipp: string }[]) => (
                      <div className="flex gap-2 flex-1">
                        {items.map(({ label, outcome, market, tipp }) => {
                          const sel = isEvSelected(ev.event_id, outcome.outcome_id, market.specifiers);
                          const oddsVal = formatOdds(outcome.odds);
                          const disabled = !outcome.active || oddsVal === '-' || parseFloat(String(outcome.odds)) < 1.01;
                          return (
                            <OddsButton
                              odds={outcome.odds}
                              selected={sel || false}
                              key={label}
                              onClick={() => handleEvOddsClick(ev, market, outcome, tipp)}
                              disabled={disabled}
                              className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 w-full border ${
                                sel
                                  ? 'bg-[#333] border-[#333]'
                                  : disabled
                                  ? 'bg-gray-50 dark:bg-[#1a1a1a] opacity-40 cursor-not-allowed border-gray-200 dark:border-[#333] dark:text-gray-500'
                                  : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0] dark:bg-[#252525] dark:border-[#333] dark:hover:bg-[#333] dark:text-white'
                              }`}
                            >
                              <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>
                                {formatOddsButtonLabel(label, ev.event.home.name, ev.event.away.name, locale)}
                              </span>
                              <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>
                                {disabled ? <LockIcon /> : oddsVal}
                              </span>
                            </OddsButton>
                          );
                        })}
                      </div>
                    );

                    const renderContent = () => {
                      const layoutGrp1 = getLayoutGroupForMarketId(activeBetFilter);
                      if (layoutGrp1 === 'Tipp') {
                        const m = find1x2Market(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '1', outcome: m.home, market: m.market, tipp: 'Heimsieg' },
                          { label: 'X', outcome: m.draw, market: m.market, tipp: 'Unentschieden' },
                          { label: '2', outcome: m.away, market: m.market, tipp: 'Auswärtssieg' },
                        ]);
                      }
                      if (layoutGrp1 === 'Tipp Restzeit') {
                        const m = findRestzeitGeneric(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '1', outcome: m.home, market: m.market, tipp: 'Restzeit Heimsieg' },
                          { label: 'X', outcome: m.draw, market: m.market, tipp: 'Restzeit Unentschieden' },
                          { label: '2', outcome: m.away, market: m.market, tipp: 'Restzeit Auswärtssieg' },
                        ]);
                      }
                      if (layoutGrp1 === 'Über/Unter') {
                        const m = findOUGeneric(ev.markets, false, ev);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '+', outcome: m.over, market: m.market, tipp: 'Über 0,5' },
                          { label: '−', outcome: m.under, market: m.market, tipp: 'Unter 0,5' },
                        ]);
                      }
                      if (layoutGrp1 === 'Nächstes Tor') {
                        const m = findNextGoal(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '1', outcome: m.home, market: m.market, tipp: 'Nächstes Tor Heim' },
                          { label: 'X', outcome: m.none, market: m.market, tipp: 'Kein Tor' },
                          { label: '2', outcome: m.away, market: m.market, tipp: 'Nächstes Tor Auswärts' },
                        ]);
                      }
                      if (layoutGrp1 === 'Handicap' || layoutGrp1 === 'Satz-Handicap') {
                        const m = findHandicap(ev.markets);
                        if (!m) {
                          const slots = layoutGrp1 === 'Satz-Handicap' ? [1, 2] : [1, 2, 3];
                          return <div className="flex gap-2 flex-1">{slots.map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        }
                        const items: { label: string; outcome: Outcome; market: Market; tipp: string }[] = [
                          { label: '1', outcome: m.o1, market: m.market, tipp: layoutGrp1 === 'Satz-Handicap' ? 'Satz-Handicap 1' : 'Handicap 1' },
                        ];
                        if (m.o3) items.push({ label: 'X', outcome: m.o3, market: m.market, tipp: 'Handicap X' });
                        items.push({ label: '2', outcome: m.o2, market: m.market, tipp: layoutGrp1 === 'Satz-Handicap' ? 'Satz-Handicap 2' : 'Handicap 2' });
                        return renderOddsButtons(items);
                      }
                      if (layoutGrp1 === 'Beide Teams treffen') {
                        const m = findBTTSGeneric(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Ja' },
                          { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Nein' },
                        ]);
                      }
                      if (layoutGrp1 === 'Halbzeit-Über/Unter') {
                        const m = findOUGeneric(ev.markets, true, ev);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '+', outcome: m.over, market: m.market, tipp: '1.Hz Über 0,5' },
                          { label: '−', outcome: m.under, market: m.market, tipp: '1.Hz Unter 0,5' },
                        ]);
                      }
                      if (layoutGrp1 === 'Doppelte Chance') {
                        const m = findDCGeneric(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '1X', outcome: m.o1x, market: m.market, tipp: '1X' },
                          { label: '12', outcome: m.o12, market: m.market, tipp: '12' },
                          { label: 'X2', outcome: m.ox2, market: m.market, tipp: 'X2' },
                        ]);
                      }
                      if (layoutGrp1 === 'Head-To-Head' || layoutGrp1 === 'Sieger' || layoutGrp1 === 'Draw No Bet') {
                        const m = findH2H(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        const tippLabelHome = layoutGrp1 === 'Sieger' ? 'Sieger Heim' : layoutGrp1 === 'Draw No Bet' ? 'Draw No Bet Heim' : 'H2H Heim';
                        const tippLabelAway = layoutGrp1 === 'Sieger' ? 'Sieger Auswärts' : layoutGrp1 === 'Draw No Bet' ? 'Draw No Bet Auswärts' : 'H2H Auswärts';
                        return renderOddsButtons([
                          { label: '1', outcome: m.home, market: m.market, tipp: tippLabelHome },
                          { label: '2', outcome: m.away, market: m.market, tipp: tippLabelAway },
                        ]);
                      }
                      if (layoutGrp1 === '1. Halbzeit') {
                        const m = find1stHalfMarket(ev.markets);
                        if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                        return renderOddsButtons([
                          { label: '1', outcome: m.home, market: m.market, tipp: '1.Hz Heimsieg' },
                          { label: 'X', outcome: m.draw, market: m.market, tipp: '1.Hz Unentschieden' },
                          { label: '2', outcome: m.away, market: m.market, tipp: '1.Hz Auswärtssieg' },
                        ]);
                      }
                      return null;
                    };

                    return (
                      <div
                        key={ev.event_id}
                        className={`px-4 py-3 ${
                          idx > 0 ? 'border-t border-gray-100' : ''
                        } hover:bg-gray-50/50 transition`}
                      >
                        {/* Teams row: Time, Teams, Scores, Stats */}
                        <div
                          className="flex items-start gap-3 cursor-pointer mb-2.5"
                          onClick={() => handleEventClick(ev)}
                        >
                          <span className="text-[13px] font-bold text-gray-800 min-w-[40px] mt-0.5 select-none">
                            {matchTime}
                          </span>
                          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                            {/* Home Team */}
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2 min-w-0">
                                <img src={ev.event.home.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                <span className="text-[14px] font-semibold text-gray-900 truncate leading-tight">
                                  {team1}
                                </span>
                              </div>
                              {hasScores && (
                                <span className="text-[13px] font-semibold text-gray-400 flex-shrink-0">
                                  ({homeScore})
                                </span>
                              )}
                            </div>
                            {/* Away Team */}
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2 min-w-0">
                                <img src={ev.event.away.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                <span className="text-[14px] font-semibold text-gray-900 truncate leading-tight">
                                  {team2}
                                </span>
                              </div>
                              {hasScores && (
                                <span className="text-[13px] font-semibold text-gray-400 flex-shrink-0">
                                  ({awayScore})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 mt-2">
                            <StatsIcon />
                          </div>
                        </div>

                        {/* Odds row: inline with moreCount */}
                        <div className="flex items-center gap-1.5 mt-2">
                          {attr && (
                            <span className="text-[10px] font-bold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 select-none">
                              {attr}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            {renderContent()}
                          </div>
                          <button
                            className="text-[12px] font-semibold text-gray-400 flex items-center gap-0.5 hover:text-gray-600 pl-1.5 flex-shrink-0"
                            onClick={() => handleEventClick(ev)}
                          >
                            <span>{moreCount}</span>
                            <span className="text-[14px] leading-none text-gray-400">›</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              // Multiple Tournaments view (e.g. "Alle Spiele"): Group by Tournament first, then inside group by Date
              leagues.map((league) => {
                const leagueEvents = displayedMatchEvents.filter((ev: EventSnapshot) => ev.event?.tournament_name === league.name);
                return (
                  <div key={league.name} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <div className="px-4 py-3 border-b border-gray-100 bg-white">
                      <span className="text-sm font-bold text-red-600">{league.name}</span>
                    </div>
                    {groupEventsByDate(leagueEvents).map((dateGroup) => (
                      <React.Fragment key={dateGroup.dateStr}>
                        <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-100 text-[11px] font-bold text-gray-500">
                          {dateGroup.dateStr}
                        </div>
                        {dateGroup.events.map((ev, idx) => {
                          const matchTime = formatEventTime(ev);
                          const team1 = ev.event?.home?.name ?? 'Home';
                          const team2 = ev.event?.away?.name ?? 'Away';
                          const moreCount = ev.markets?.length ?? 0;
                          const homeJersey = getJerseyColors(team1, true);
                          const awayJersey = getJerseyColors(team2, false);
                          const homeScore = ev.status?.home_score;
                          const awayScore = ev.status?.away_score;
                          const hasScores = typeof homeScore === 'number' && typeof awayScore === 'number';

                          let attrMarket: Market | undefined;
                          const layoutGrp2 = getLayoutGroupForMarketId(activeBetFilter);
                          if (layoutGrp2 === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Handicap' || layoutGrp2 === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === 'Head-To-Head' || layoutGrp2 === 'Sieger' || layoutGrp2 === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp2 === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                          const attr = attrMarket ? getMarketAttr(attrMarket) : null;

                          const renderOddsButtons = (items: { label: string; outcome: Outcome; market: Market; tipp: string }[]) => (
                            <div className="flex gap-2 flex-1">
                              {items.map(({ label, outcome, market, tipp }) => {
                                const sel = isEvSelected(ev.event_id, outcome.outcome_id, market.specifiers);
                                const oddsVal = formatOdds(outcome.odds);
                                const disabled = !outcome.active || oddsVal === '-' || parseFloat(String(outcome.odds)) < 1.01;
                                return (
                                  <OddsButton
                                    odds={outcome.odds}
                                    selected={sel || false}
                                    key={label}
                                    onClick={() => handleEvOddsClick(ev, market, outcome, tipp)}
                                    disabled={disabled}
                                    className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 w-full border ${
                                      sel
                                        ? 'bg-[#333] border-[#333]'
                                        : disabled
                                        ? 'bg-gray-50 dark:bg-[#1a1a1a] opacity-40 cursor-not-allowed border-gray-200 dark:border-[#333] dark:text-gray-500'
                                        : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0] dark:bg-[#252525] dark:border-[#333] dark:hover:bg-[#333] dark:text-white'
                                    }`}
                                  >
                                    <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>
                                      {formatOddsButtonLabel(label, ev.event.home.name, ev.event.away.name, locale)}
                                    </span>
                                    <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>
                                      {disabled ? <LockIcon /> : oddsVal}
                                    </span>
                                  </OddsButton>
                                );
                              })}
                            </div>
                          );

                          const renderContent = () => {
                            const layoutGrp2 = getLayoutGroupForMarketId(activeBetFilter);
                            if (layoutGrp2 === 'Tipp') {
                              const m = find1x2Market(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '1', outcome: m.home, market: m.market, tipp: 'Heimsieg' },
                                { label: 'X', outcome: m.draw, market: m.market, tipp: 'Unentschieden' },
                                { label: '2', outcome: m.away, market: m.market, tipp: 'Auswärtssieg' },
                              ]);
                            }
                            if (layoutGrp2 === 'Tipp Restzeit') {
                              const m = findRestzeitGeneric(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '1', outcome: m.home, market: m.market, tipp: 'Restzeit Heimsieg' },
                                { label: 'X', outcome: m.draw, market: m.market, tipp: 'Restzeit Unentschieden' },
                                { label: '2', outcome: m.away, market: m.market, tipp: 'Restzeit Auswärtssieg' },
                              ]);
                            }
                            if (layoutGrp2 === 'Über/Unter') {
                              const m = findOUGeneric(ev.markets, false, ev);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '+', outcome: m.over, market: m.market, tipp: 'Über 0,5' },
                                { label: '−', outcome: m.under, market: m.market, tipp: 'Unter 0,5' },
                              ]);
                            }
                            if (layoutGrp2 === 'Nächstes Tor') {
                              const m = findNextGoal(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '1', outcome: m.home, market: m.market, tipp: 'Nächstes Tor Heim' },
                                { label: 'X', outcome: m.none, market: m.market, tipp: 'Kein Tor' },
                                { label: '2', outcome: m.away, market: m.market, tipp: 'Nächstes Tor Auswärts' },
                              ]);
                            }
                            if (layoutGrp2 === 'Handicap' || layoutGrp2 === 'Satz-Handicap') {
                              const m = findHandicap(ev.markets);
                              if (!m) {
                                const slots = layoutGrp2 === 'Satz-Handicap' ? [1, 2] : [1, 2, 3];
                                return <div className="flex gap-2 flex-1">{slots.map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              }
                              const items: { label: string; outcome: Outcome; market: Market; tipp: string }[] = [
                                { label: '1', outcome: m.o1, market: m.market, tipp: layoutGrp2 === 'Satz-Handicap' ? 'Satz-Handicap 1' : 'Handicap 1' },
                              ];
                              if (m.o3) items.push({ label: 'X', outcome: m.o3, market: m.market, tipp: 'Handicap X' });
                              items.push({ label: '2', outcome: m.o2, market: m.market, tipp: layoutGrp2 === 'Satz-Handicap' ? 'Satz-Handicap 2' : 'Handicap 2' });
                              return renderOddsButtons(items);
                            }
                            if (layoutGrp2 === 'Beide Teams treffen') {
                              const m = findBTTSGeneric(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Ja' },
                                { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Nein' },
                              ]);
                            }
                            if (layoutGrp2 === 'Halbzeit-Über/Unter') {
                              const m = findOUGeneric(ev.markets, true, ev);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '+', outcome: m.over, market: m.market, tipp: '1.Hz Über 0,5' },
                                { label: '−', outcome: m.under, market: m.market, tipp: '1.Hz Unter 0,5' },
                              ]);
                            }
                            if (layoutGrp2 === 'Doppelte Chance') {
                              const m = findDCGeneric(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '1X', outcome: m.o1x, market: m.market, tipp: '1X' },
                                { label: '12', outcome: m.o12, market: m.market, tipp: '12' },
                                { label: 'X2', outcome: m.ox2, market: m.market, tipp: 'X2' },
                              ]);
                            }
                            if (layoutGrp2 === 'Head-To-Head' || layoutGrp2 === 'Sieger' || layoutGrp2 === 'Draw No Bet') {
                              const m = findH2H(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              const tippLabelHome = layoutGrp2 === 'Sieger' ? 'Sieger Heim' : layoutGrp2 === 'Draw No Bet' ? 'Draw No Bet Heim' : 'H2H Heim';
                              const tippLabelAway = layoutGrp2 === 'Sieger' ? 'Sieger Auswärts' : layoutGrp2 === 'Draw No Bet' ? 'Draw No Bet Auswärts' : 'H2H Auswärts';
                              return renderOddsButtons([
                                { label: '1', outcome: m.home, market: m.market, tipp: tippLabelHome },
                                { label: '2', outcome: m.away, market: m.market, tipp: tippLabelAway },
                              ]);
                            }
                            if (layoutGrp2 === '1. Halbzeit') {
                              const m = find1stHalfMarket(ev.markets);
                              if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                              return renderOddsButtons([
                                { label: '1', outcome: m.home, market: m.market, tipp: '1.Hz Heimsieg' },
                                { label: 'X', outcome: m.draw, market: m.market, tipp: '1.Hz Unentschieden' },
                                { label: '2', outcome: m.away, market: m.market, tipp: '1.Hz Auswärtssieg' },
                              ]);
                            }
                            return null;
                          };

                          return (
                            <div
                              key={ev.event_id}
                              className={`p-4 ${
                                idx > 0 ? 'border-t border-gray-100/70' : ''
                              } hover:bg-gray-50/50 transition`}
                            >
                              {/* Top row: Time, Teams, Scores, Stats */}
                              <div className="flex items-center justify-between mb-3.5">
                                <div
                                  className="flex items-center gap-3.5 flex-1 min-w-0 cursor-pointer"
                                  onClick={() => handleEventClick(ev)}
                                >
                                  <span className="text-[13px] font-bold text-gray-800 min-w-[38px] select-none">
                                    {matchTime}
                                  </span>
                                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                                    {/* Home Team */}
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <img src={ev.event.home.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                        <span className="text-[14px] font-bold text-gray-900 truncate leading-tight">
                                          {team1}
                                        </span>
                                      </div>
                                      {hasScores && (
                                        <span className="text-[13px] font-bold text-gray-500 mr-2 flex-shrink-0">
                                          ({homeScore})
                                        </span>
                                      )}
                                    </div>
                                    {/* Away Team */}
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <img src={ev.event.away.crest_url || '/default-crest.svg'} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                                        <span className="text-[14px] font-bold text-gray-900 truncate leading-tight">
                                          {team2}
                                        </span>
                                      </div>
                                      {hasScores && (
                                        <span className="text-[13px] font-bold text-gray-500 mr-2 flex-shrink-0">
                                          ({awayScore})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex-shrink-0 ml-3 mr-1" onClick={() => handleEventClick(ev)}>
                                  <StatsIcon />
                                </div>
                              </div>

                              {/* Bottom row: Attributes, Odds buttons, More markets */}
                              <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-gray-100/70">
                                <div className="flex-1 flex items-center gap-2 max-w-[280px]">
                                  {attr && (
                                    <span className="text-[10px] font-bold text-gray-900 bg-gray-100 px-1.5 py-1 rounded flex-shrink-0 select-none">
                                      {attr}
                                    </span>
                                  )}
                                  {renderContent()}
                                </div>
                                <button
                                  className="text-[11px] font-bold text-gray-400 flex items-center justify-center gap-0.5 hover:text-gray-600 pl-2 pr-1 h-9 flex-shrink-0"
                                  onClick={() => handleEventClick(ev)}
                                >
                                  <span>{moreCount}</span>
                                  <span className="text-[14px] leading-none text-gray-400 font-normal">›</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── VIEW 2: Tournament list ──────────────────────────────────────────────

  if (selectedCategory) {
    const totalCount = selectedCategory.count;
    return (
      <div className="min-h-screen bg-gray-100 pb-20">
        {renderHeader(`${sportLabel} ${selectedCategory.name}`, () => setSelectedCategory(null))}
        {renderTimeTabs(() => setSelectedCategory(null))}

        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
          </div>
        )}

        {!loading && (
          <div className="mt-3 bg-white">
            {/* Alle Spiele row */}
            <button
              onClick={() => { setSelectedTournament(null); navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(selectedCategory.name)}/alle`); }}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 transition border-b border-gray-100"
            >
              <CountryFlag countryName={selectedCategory.name} countryCode={selectedCategory.countryCode} fallbackEmoji={selectedCategory.flag} className="text-2xl text-center flex-shrink-0 leading-none" />
              <span className="flex-1 text-[15px] font-bold text-gray-900">Alle Spiele</span>
              <span className="text-[15px] font-medium text-gray-600">{totalCount}</span>
            </button>

            {/* Section label */}
            {tournaments.length > 0 && (
              <div className="px-4 pt-4 pb-2">
                <span className="text-[13px] font-bold text-gray-900">Spiel</span>
              </div>
            )}

            {/* Tournament rows */}
            {tournaments.map((t, idx, arr) => (
              <button
                key={t.name}
                onClick={() => { navigate(`/sport/${encodeURIComponent(sportLabel)}/${encodeURIComponent(selectedCategory.name)}/${encodeURIComponent(t.name)}`); }}
                className={`w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 transition ${idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
              >
                <CountryFlag countryName={selectedCategory.name} countryCode={selectedCategory.countryCode} fallbackEmoji={t.flag} className="text-2xl text-center flex-shrink-0 leading-none" />
                <span className="flex-1 text-[15px] font-medium text-gray-900 truncate pr-2">{t.name}</span>
                <span className="text-[15px] font-medium text-gray-500 flex-shrink-0">{t.count}</span>
              </button>
            ))}
          </div>
        )}

        {!loading && tournaments.length === 0 && (
          <p className="text-center text-gray-500 text-sm mt-10">Keine Ereignisse verfügbar.</p>
        )}
      </div>
    );
  }

  // ── VIEW 1: Category list ────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {renderHeader(sportLabel, onBack)}
      {renderTimeTabs()}
      <div className="px-3 pt-4 flex flex-col gap-4">
        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
          </div>
        )}
        {!loading && categories.length === 0 && (
          <p className="text-center text-gray-500 text-sm mt-10">Keine Ereignisse verfügbar.</p>
        )}
        {!loading && topCategories.length > 0 && (
          <div>
            <h2 className="text-base font-black text-gray-900 mb-2 px-1">Top</h2>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {topCategories.map((cat, idx, arr) => renderCategoryRow(cat, idx, arr))}
            </div>
          </div>
        )}
        {!loading && azCategories.length > 0 && (
          <div>
            <h2 className="text-base font-black text-gray-900 mb-2 px-1">A-Z</h2>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {azCategories.map((cat, idx, arr) => renderCategoryRow(cat, idx, arr))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SportMatchesPage;
